import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { TenantResumen } from '../api';
import { listarTenants } from './api';

/** Clave de la lista de tenants. Constante para que consultar e invalidar no se separen. */
export const CLAVE_TENANTS = ['tenants'] as const;

/** Clave de la lista filtrada a los que hoy pueden emitir. Aparte: es OTRO pedido, con otro filtro. */
export const CLAVE_TENANTS_EMISORES = ['tenants', 'emisores'] as const;

/**
 * Los tenants para elegir por nombre, en el ABM de sistemas.
 *
 * `staleTime` alto a propósito: la cartera de clientes cambia con un alta de
 * tenant, que es una operación rara y de otra pantalla. Volver a pedirla en cada
 * montaje sería pagar un pedido por cada fila del ABM que abre su lista blanca,
 * para ver siempre lo mismo.
 */
export function useTenants(): UseQueryResult<TenantResumen[], Error> {
  return useQuery({
    queryKey: CLAVE_TENANTS,
    queryFn: () => listarTenants(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Los tenants para el selector de la pantalla de emisión (KAN-145): **solo**
 * los que hoy pueden emitir (`soloEmisores=true`). Es una query aparte de
 * {@link useTenants} y no la misma filtrada del lado del cliente: son pedidos
 * distintos al backend, con resultados que no coinciden (el ABM de sistemas
 * necesita ver también al tenant con la delegación en trámite), así que
 * comparten clave sería mezclar dos respuestas distintas bajo un mismo caché.
 */
export function useTenantsParaEmitir(): UseQueryResult<TenantResumen[], Error> {
  return useQuery({
    queryKey: CLAVE_TENANTS_EMISORES,
    queryFn: () => listarTenants({ soloEmisores: true }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Índice `id → tenant` para traducir los `tenantsAutorizados` de un sistema, que
 * el contrato devuelve como ids desnudos.
 */
export function porId(tenants: readonly TenantResumen[]): Map<number, TenantResumen> {
  return new Map(tenants.map((t) => [t.id, t]));
}
